import React from 'react'
import { useDispatch } from 'react-redux';
import { useState, useEffect } from 'react';
import api from '../../../axios/api';
import { Card, TextField, CardContent } from '@mui/material';
import CustomButton from '../../../componentsII/Buttons';
import { showNotification } from '../../../redux/slices/notificationSlice';
import { FormControl, MenuItem, Select } from '@mui/material';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const AddUpdateHeatSteamEmissionModal = ({ onClose, refetch, initialData = null }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();

  const [units, setUnits] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    unitId: initialData?.unit_id || '',
    emission: initialData?.emission_kgco2e || '',
    year: initialData?.valid_year ? new Date(initialData.valid_year, 0) : new Date(new Date().getFullYear(), 0),
    consumptionType: initialData?.consumption_type || null
  });

  const consumptionTypes = [
    { id: 'ONSITE_HEATSTEAM', label: 'Onsite Heat Steam' },
    { id: 'DISTRICT_HEATSTEAM', label: 'District Heat Steam' }
  ];

  const validate = (values) => {
    const newErrors = {};

    // Unit validation
    if (!values.unitId) {
      newErrors.unitId = 'Unit is required';
    }

    // Emission validation
    if (!values.emission) {
      newErrors.emission = 'Emission value is required';
    } else if (isNaN(values.emission)) {
      newErrors.emission = 'Emission must be a number';
    } else if (Number(values.emission) < 0) {
      newErrors.emission = 'Emission cannot be negative';
    }

    // Year validation
    if (!values.year) {
      newErrors.year = 'Year is required';
    }

    // Consumption Type validation
    if (!values.consumptionType) {
      newErrors.consumptionType = 'Consumption type is required';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        id: initialData?.id || 0,
        unitId: formData.unitId,
        emission: formData.emission,
        year: formData.year ? formData.year.getFullYear() : null,
        consumptionType: formData.consumptionType
      };

      const response = await api.post('/admin/createUpdateHeatSteamEmission', payload);
      if (response.data.status === '200') {
        dispatch(showNotification({
          message: response.data.message,
          type: "success"
        }));
        onClose();
        refetch?.();
      } else {
        setErrors(prev => ({
          ...prev,
          general: response.data.message || 'Something went wrong'
        }));
      }
    } catch (error) {
      dispatch(showNotification({
        message: error.response?.data?.message || 'An error occurred',
        type: "ERROR"
      }));
      setErrors({ submit: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const getUnits = async () => {
    try {
      const response = await api.get('/common/measurementType?type_code=ELECTRICITY');
      if (response.status === 200) {
        setUnits(response.data.data);
      }
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    getUnits();
  }, []);

  return (
    <Card className="w-full mx-auto">
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="consumptionType" className="text-sm font-medium block">
              Consumption Type<span className="text-red-500">*</span>
            </label>
            <FormControl fullWidth size="small">
              <Select
                labelId="consumptionType-label"
                id="consumptionType"
                name="consumptionType"
                value={formData.consumptionType}
                onChange={handleInputChange}
                disabled={isSubmitting}
                error={Boolean(errors.consumptionType)}
                className={'w-full'}
              >
                {consumptionTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {errors.consumptionType && (
              <p className="text-sm text-red-500 mt-1">{errors.consumptionType}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="unitId" className="text-sm font-medium block">
              Unit<span className="text-red-500">*</span>
            </label>
            <FormControl fullWidth size="small">
              <Select
                labelId="unit-label"
                id="unitId"
                name="unitId"
                value={formData.unitId}
                onChange={handleInputChange}
                disabled={isSubmitting}
                error={Boolean(errors.unitId)}
                className={'w-full'}
              >
                {units.map((unit) => (
                  <MenuItem key={unit.id} value={unit.id}>
                    {unit.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {errors.unitId && (
              <p className="text-sm text-red-500 mt-1">{errors.unitId}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="emission" className="text-sm font-medium">
              kgCo2e Emission<span className="text-red-500">*</span>
            </label>
            <TextField
              size="small"
              id="emission"
              name="emission"
              value={formData.emission}
              onChange={handleInputChange}
              placeholder="Enter Emission Value kgCo2e"
              disabled={isSubmitting}
              error={Boolean(errors.emission)}
              className={'w-full'}
              type="number"
            />
            {errors.emission && (
              <p className="text-sm text-red-500 mt-1">{errors.emission}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="yearPicker" className="text-sm font-medium block">
              Select Year<span className="text-red-500">*</span>
            </label>
            <DatePicker
              id="yearPicker"
              selected={formData.year}
              onChange={(date) => handleInputChange({ target: { name: "year", value: date } })}
              showYearPicker
              dateFormat="yyyy"
              className="w-full border border-gray-300 rounded-md p-2 text-sm"
              wrapperClassName="w-full"
              disabled={isSubmitting}
            />
            {errors.year && <p className="text-sm text-red-500 mt-1">{errors.year}</p>}
          </div>

          <div className="flex justify-end gap-4 pt-4">
            <CustomButton
              className="bg-gray-500 hover:bg-gray-700 text-white"
              text="Cancel"
              click={onClose}
            />
            <CustomButton
              className="bg-green-600 hover:bg-green-700 text-white"
              text={initialData?.id ? "Update" : "Submit"}
              type="submit"
              disabled={isSubmitting}
            />
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

export default AddUpdateHeatSteamEmissionModal;